<template>
  <div class="clip-cue-controls" data-qa="container-clip-cue-controls">
    <KisweButton
      v-if="cueType === CueType.CueIn"
      :theme="cueTheme"
      :disabled="isProgramScene"
      shape="square"
      :size="KisweSize.Small"
      @click="setCueAtPlayhead"
      class="clip-cue-controls__cue-in-btn"
      :data-qa="`button-set-cue-in-${sceneId}`"
    ><KisweIcon name="cue-in" /></KisweButton>
    <KisweInput
      :type="KisweInputType.Duration"
      :size="KisweSize.Small"
      :disabled="isProgramScene"
      :modelValue="localCueTime"
      @change="onDurationChange"
      :class="{
        'clip-cue-controls__cue-in-time': cueType === CueType.CueIn,
        'clip-cue-controls__cue-out-time': cueType === CueType.CueOut,
        'clip-cue-controls__cue-time--disabled': isProgramScene
      }"
      :data-qa="`input-cue-time-${sceneId}`"
    />
    <KisweButton
      v-if="cueType === CueType.CueOut"
      :theme="cueTheme"
      :disabled="isProgramScene"
      shape="square"
      :size="KisweSize.Small"
      @click="setCueAtPlayhead"
      class="clip-cue-controls__cue-out-btn"
      :data-qa="`button-set-cue-out-${sceneId}`"
    ><KisweIcon name="cue-out" /></KisweButton>
  </div>
</template>

<script lang="ts">
import './ClipCueControls.scss'
import { computed, defineComponent, ref, toRefs, watch, nextTick } from 'vue'
import { KisweButton, KisweIcon, KisweInput, KisweTheme, KisweSize, KisweInputType } from 'kiswe-ui'
import { useCurrentCast } from '@/modules/casts/compositions'
import { SceneSourceType } from '@/modules/scenes/types'
import { useClipInfo, useVideoPlayer } from '@/modules/video/compositions'
import { CueType } from '../../types'

export default defineComponent({
  name: 'ClipCueControls',
  components: {
    KisweButton,
    KisweIcon,
    KisweInput
  },
  props: {
    sceneId: {
      type: String,
      required: true
    },
    cueType: {
      type: String as () => CueType,
      required: false,
      default: CueType.CueIn
    }
  },
  // eslint-disable-next-line max-lines-per-function
  setup (props) {
    const { currentCast, currentCastId } = useCurrentCast()
    const { sceneId, cueType } = toRefs(props)

    const isProgramScene = computed(() => currentCast.value?.programSceneId === sceneId.value)
    const playerName = computed(() => {
      if (currentCast.value?.getSceneSourceType(sceneId.value) !== SceneSourceType.Playlist) return ''
      else return 'player1'
    })
    const { currentClipPlayId, setClipCueIn, setClipCueOut } = useVideoPlayer(playerName)
    const { getClipCue, getClipDelayedPlayhead } = useClipInfo(currentCastId, playerName)
    const clipCue = computed(() => currentClipPlayId.value === null
      ? 0 : getClipCue(currentClipPlayId.value, cueType.value))
    const clipPlayhead = computed(() => currentClipPlayId.value === null ? 0 : getClipDelayedPlayhead())
    const cueTheme = computed(() => clipCue.value !== 0 ? KisweTheme.Primary : KisweTheme.Tertiary)
    // localCueTime is not set as v-model as it otherwise ends up immediately updating `duration` KisweInput
    // on input of first character which confuses users. KisweInput is updated on `change` event
    const localCueTime = ref(clipCue.value)

    const setCueTime = async (cueTime: number) => {
      const result =  (cueType.value === CueType.CueIn) ? await setClipCueIn(cueTime, currentClipPlayId.value)
        : await setClipCueOut(cueTime, currentClipPlayId.value)
      result.logIfError(`Error setting cue time at ${cueTime}s for clip ${currentClipPlayId.value}`)
      return result.isSuccess
    }
    const setCueAtPlayhead = async () => {
      const cue = clipCue.value === 0 ? clipPlayhead.value : 0
      await setCueTime(cue)
    }
    const onDurationChange = async (value: string) => {
      const newDuration = Number.parseInt(value)
      if (newDuration === clipCue.value) return
      const result = await setCueTime(newDuration)
      if (!result) {
        // Incrementing and resetting immediately to fire reactivity code, else KisweInput does not reset
        localCueTime.value = clipCue.value + 1
        nextTick(() => { localCueTime.value = clipCue.value })
        return
      }
    }

    watch(clipCue, (newVal: number) => {
      if (newVal === localCueTime.value) return
      localCueTime.value = newVal
    })

    return {
      cueTheme,
      CueType,
      isProgramScene,
      localCueTime,
      onDurationChange,
      setCueAtPlayhead,
      KisweSize,
      KisweInputType
    }
  }
})
</script>
